import { observable, action } from 'mobx';
import { UserProfile } from '@app/Models/User';

type JwtToken = {
    aio: string;
    amr: string[];
    aud: string;
    exp: number;
    family_name: string;
    given_name: string;
    iat: number;
    ipaddr: string;
    iss: string;
    name: string;
    nbf: number;
    nonce: string;
    oid: string;
    rh: string;
    roles: string[];
    sub: string;
    tid: string;
    unique_name: string;
    preferred_username: string;
    upn: string;
    uti: string;
    ver: string;
};

class AppStore {
    @observable isLoading: boolean = false;
    ajaxCount: number = 0;
    @observable isHistorized: boolean = false;
    @observable isDisabledChangeLog: boolean = false;
    @observable isCtrlKey: boolean = false;
    @observable isShiftKey: boolean = false;
    actionElement: HTMLInputElement | null = null;
    actionElementMap: Map<number, HTMLInputElement> = new Map<number, HTMLInputElement>();

    constructor() {
        document.addEventListener('click', this._appClickHandler.bind(this));
        document.body.addEventListener('keydown', this._keydownHandler);
        document.body.addEventListener('mouseenter', this._appMouseInHandler);
    }

    public get currentToken(): string {
        return this.getValue('currentToken');
    }

    public get currentUser(): UserProfile {
        return this.getValue('currentUser');
    }

    @observable private _permissionImpersonation: IPermissionImpersonation | null = this.getValue('permissionImpersonation');

    public get permissionImpersonation(): IPermissionImpersonation | null {
        return this._permissionImpersonation;
    }

    @action
    setPermissionImpersonation(value: IPermissionImpersonation) {
        this.setValue('permissionImpersonation', value);
        this._permissionImpersonation = value;
    }

    @action
    clearPermissionImpersonation() {
        localStorage.removeItem('permissionImpersonation');
        this._permissionImpersonation = null;
    }

    public get impersonationRolesHeader(): string {
        return this.permissionImpersonation?.encoded || '';
    }

    /**
     * Display name of the signed-in user. A v1 id_token carries unique_name, a v2 access token
     * preferred_username, so both have to be considered.
     */
    public get currentUserName(): string {
        const user = this.currentUser;
        return user ? user.name || user.preferred_username || user.unique_name || '' : '';
    }

    public get isMacOS(): boolean {
        return window.navigator.platform.includes('Mac');
    }

    @action
    showLoading() {
        this.ajaxCount++;
        if (!this.isLoading) {
            this.isLoading = true;
        }
    }

    @action
    hideLoading() {
        this.ajaxCount--;
        if (this.ajaxCount === 0) {
            this.isLoading = false;
        }
    }

    public get currentTokenObj () {
        return this._parseJwtToken(this.currentToken);
    }

    private _parseJwtToken (token: string): JwtToken | null {
        if (!token) return null;

        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64).split('').map((c: string) => {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join('')
        );

        return JSON.parse(jsonPayload);
    }

    @action
    isAuthorized(): boolean {
        return !!(this.currentToken && this.currentUser);
    }

    @action
    clearUserData() {
        localStorage.removeItem('currentUser');
        localStorage.removeItem('currentToken');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('pkce_code_verifier');
        localStorage.removeItem('pkce_state');
        localStorage.removeItem('redirectUri');
    }

    @action
    setValue<T>(key: string, value: T) {
        localStorage.setItem(key, JSON.stringify(value));
    }

    @action
    getValue<T>(key: string) {
        const value = localStorage.getItem(key);
        if (value === null) return null;

        // adal.js stores the JWT raw, unlike everything else in here. The quote stripping covers a browser that
        // has not reloaded since the upgrade and still holds the previous JSON-quoted value.
        if (key === 'currentToken') return value.replace(/^"|"$/g, '');

        return JSON.parse(value);
    }

    showAjaxSpinner(timestamp: number) {
        const elem = this.actionElement;
        if (elem) {
            this.actionElementMap.set(timestamp, elem);
            elem.disabled = true;
            elem.classList.add('btn-loader');
        }
    }

    hideAjaxSpinner(timestamp: number) {
        const elem = this.actionElementMap.get(timestamp);
        if (elem) {
            elem.disabled = false;
            elem.classList.remove('btn-loader');
            this.actionElementMap.delete(timestamp);
            this.actionElement = null;
        }
    }

    @action.bound
    _appClickHandler(event: MouseEvent) {
		const target = event.target as HTMLInputElement;
		if (target && target.type === 'button') {
			this.actionElement = target;
		}
    }

    @action.bound
    _keydownHandler(event: KeyboardEvent) {
        /* Windows Ctrl key is MacOS Command key.  Keep in mind to check isMacOS.  event.metaKey in Windows is WINDOWS key.*/
        this.isCtrlKey = event.ctrlKey || (this.isMacOS && event.metaKey);
    }

    @action.bound
    _appMouseInHandler(event: MouseEvent) {
        this.isCtrlKey = event.ctrlKey || (this.isMacOS && event.metaKey);
        this.isShiftKey = event.shiftKey;
    }
}

export interface IPermissionImpersonation {
    permissionIds: number[];
    encoded: string;
}

export const appStore = new AppStore();
